import { create } from "zustand";

export interface TrainingItem {
  slug: string;
  title: string;
  languageCode: string;
  level: string;
  speakers: string;
  unlockedAt: string;
  masteredAt?: string;
}

const STORAGE_KEY = "glossika-training-list";

function load(): TrainingItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as TrainingItem[];
  } catch {
    return [];
  }
}

function save(items: TrainingItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

interface TrainingState {
  items: TrainingItem[];
  loaded: boolean;
  loadTraining: () => void;
  addDialog: (item: Omit<TrainingItem, "unlockedAt">) => void;
  removeDialog: (slug: string) => void;
  isUnlocked: (slug: string) => boolean;
  markMastered: (slug: string) => void;
  isMastered: (slug: string) => boolean;
  masteredSlugs: () => Set<string>;
}

export const useTrainingStore = create<TrainingState>((set, get) => ({
  items: [],
  loaded: false,

  loadTraining: () => {
    set({ items: load(), loaded: true });
  },

  addDialog: (item) => {
    const { items } = get();
    if (items.some((i) => i.slug === item.slug)) return;
    const updated = [{ ...item, unlockedAt: new Date().toISOString() }, ...items];
    save(updated);
    set({ items: updated });
  },

  removeDialog: (slug) => {
    const updated = get().items.filter((i) => i.slug !== slug);
    save(updated);
    set({ items: updated });
  },

  isUnlocked: (slug) => {
    return get().items.some((i) => i.slug === slug);
  },

  markMastered: (slug) => {
    const items = get().items;
    const idx = items.findIndex((i) => i.slug === slug);
    if (idx === -1 || items[idx]!.masteredAt) return;
    const updated = [...items];
    updated[idx] = { ...updated[idx]!, masteredAt: new Date().toISOString() };
    save(updated);
    set({ items: updated });
  },

  isMastered: (slug) => {
    return get().items.some((i) => i.slug === slug && !!i.masteredAt);
  },

  masteredSlugs: () => {
    return new Set(get().items.filter((i) => i.masteredAt).map((i) => i.slug));
  },
}));
